"use client";

import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import X from "@/assets/icons/x.svg";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type DietInfoDialogProps = {
  title: string;
  trigger: ReactNode;
  children: ReactNode;
};

/**
 * Shell shared by the informational dialogs opened from the diet card:
 * a fixed header with the title and a close button, plus a scrollable body.
 * Full screen below `sm`, a centered panel above it.
 */
export const DietInfoDialog = ({
  title,
  trigger,
  children,
}: DietInfoDialogProps) => {
  const t = useTranslations("rating");

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 max-sm:fixed max-sm:inset-0 max-sm:h-full max-sm:max-h-[100dvh] max-sm:max-w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none sm:h-[624px] sm:w-[864px] sm:max-w-[864px]"
        closeButton={false}
      >
        <DialogHeader className="flex h-fit flex-row items-center justify-between border-brand-grey2-300 border-b px-4 py-4 md:px-6">
          <DialogTitle className="w-full text-center font-medium text-xl sm:text-left">
            {title}
          </DialogTitle>
          <DialogClose aria-label={t("close")}>
            <X className="h-6 w-6" />
          </DialogClose>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};
